import React, { Component, ReactNode } from "react";
import Logger from "@/utils/Logger";
import { AppError, ErrorCategory, ErrorSeverity } from "@/utils/errors";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (
    error: Error,
    errorInfo: React.ErrorInfo,
    reset: () => void,
  ) => ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 * Follows React best practices for error boundaries
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({
      errorInfo,
    });

    // Log error with appropriate severity
    if (error instanceof AppError) {
      Logger.error(
        `ErrorBoundary caught ${error.category} error`,
        error,
        ["ErrorBoundary"],
        error.toJSON(),
      );
    } else {
      Logger.error(
        "ErrorBoundary caught unhandled error",
        error,
        ["ErrorBoundary"],
        {
          componentStack: errorInfo.componentStack,
        },
      );
    }

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  resetErrorBoundary = (): void => {
    this.props.onReset?.();
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback } = this.props;

    if (hasError && error && errorInfo) {
      if (fallback) {
        return fallback(error, errorInfo, this.resetErrorBoundary);
      }

      return (
        <DefaultErrorFallback error={error} reset={this.resetErrorBoundary} />
      );
    }

    return children;
  }
}

/**
 * Default error fallback UI component
 */
const DefaultErrorFallback: React.FC<{
  error: Error;
  reset: () => void;
}> = ({ error, reset }) => {
  const isAppError = error instanceof AppError;
  const severity = isAppError ? error.severity : ErrorSeverity.MEDIUM;
  const category = isAppError ? error.category : ErrorCategory.UNKNOWN;

  const getSeverityColor = (): string => {
    switch (severity) {
      case ErrorSeverity.CRITICAL:
        return "text-red-700 bg-red-50 border-red-300";
      case ErrorSeverity.HIGH:
        return "text-orange-700 bg-orange-50 border-orange-300";
      case ErrorSeverity.MEDIUM:
        return "text-yellow-700 bg-yellow-50 border-yellow-300";
      default:
        return "text-blue-700 bg-blue-50 border-blue-300";
    }
  };

  const getCategoryIcon = (): string => {
    switch (category) {
      case ErrorCategory.NETWORK:
        return "icon-cloud_off";
      case ErrorCategory.AUTHENTICATION:
      case ErrorCategory.AUTHORIZATION:
        return "icon-lock";
      case ErrorCategory.VALIDATION:
        return "icon-warning";
      default:
        return "icon-error";
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
      <div
        className={`max-w-2xl w-full p-8 border-2 rounded-lg shadow-lg ${getSeverityColor()}`}
      >
        <div className="flex items-center mb-4">
          <span className={`${getCategoryIcon()} text-4xl mr-4`} />
          <div>
            <h1 className="text-2xl font-bold mb-2">
              {isAppError
                ? `${category.toUpperCase()} Error`
                : "Something went wrong"}
            </h1>
            <p className="text-lg font-medium">{error.message}</p>
          </div>
        </div>

        {isAppError && error.code && (
          <div className="mb-4">
            <span className="font-semibold">Error Code:</span> {error.code}
          </div>
        )}

        {process.env.NODE_ENV === "development" && error.stack && (
          <details className="mt-4">
            <summary className="cursor-pointer font-semibold mb-2">
              Stack Trace (Development Only)
            </summary>
            <pre className="text-sm overflow-auto bg-gray-900 text-gray-100 p-4 rounded">
              {error.stack}
            </pre>
          </details>
        )}

        <div className="mt-6 flex gap-4">
          <button
            onClick={reset}
            className="px-6 py-2 bg-blue text-white rounded hover:bg-blue-700 transition-colors font-medium"
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors font-medium"
          >
            Reload Page
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundary;
